import type {
  Ingredient,
  CreateIngredientRequest,
  UpdateIngredientRequest,
  GetIngredientsRequest,
  IngredientsResponse,
} from '../models/Ingredient';

export class IngredientClient {
  constructor(
    private readonly baseUrl: string,
    private readonly apiKey: string,
  ) {}

  private getHeaders() {
    return {
      'Content-Type': 'application/json',
      'X-API-KEY': this.apiKey,
    };
  }

  async createIngredient(request: CreateIngredientRequest): Promise<Ingredient> {
    const response = await fetch(`${this.baseUrl}/ingredient`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`Failed to create ingredient: ${response.statusText}`);
    }

    return await response.json();
  }

  async getIngredients(): Promise<IngredientsResponse> {
    const response = await fetch(`${this.baseUrl}/ingredient`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get ingredients: ${response.statusText}`);
    }

    return await response.json();
  }

  async getIngredient(ingredientName: string, version?: number): Promise<Ingredient> {
    const url = new URL(`${this.baseUrl}/ingredient/${encodeURIComponent(ingredientName)}`);
    if (version !== undefined) {
      url.searchParams.set('version', version.toString());
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get ingredient: ${response.statusText}`);
    }

    return await response.json();
  }

  async updateIngredient(ingredientName: string, request: UpdateIngredientRequest): Promise<Ingredient> {
    const response = await fetch(`${this.baseUrl}/ingredient/${encodeURIComponent(ingredientName)}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`Failed to update ingredient: ${response.statusText}`);
    }

    return await response.json();
  }

  async deleteIngredient(ingredientName: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/ingredient/${encodeURIComponent(ingredientName)}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to delete ingredient: ${response.statusText}`);
    }
  }

  async getIngredientsByNames(request: GetIngredientsRequest): Promise<IngredientsResponse> {
    const response = await fetch(`${this.baseUrl}/ingredient/search`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`Failed to search ingredients: ${response.statusText}`);
    }

    return await response.json();
  }

  async bulkCreateIngredients(
    ingredients: CreateIngredientRequest[],
  ): Promise<{ successful: number; failed: number; errors: string[] }> {
    const results = { successful: 0, failed: 0, errors: [] as string[] };

    for (const ingredient of ingredients) {
      try {
        await this.createIngredient(ingredient);
        results.successful++;
      } catch (error) {
        results.failed++;
        results.errors.push(
          `Failed to create ingredient "${ingredient.ingredientName}": ${error instanceof Error ? error.message : 'Unknown error'}`,
        );
      }
    }

    return results;
  }
}
